import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getProjects, createProject, updateProject, deleteProject,
  getProducts, deleteProduct, updateProduct, createProduct,
  getEvents, createEvent, updateEvent, deleteEvent,
  getArt, createArt, updateArt, deleteArt,
  getFurniture, createFurniture, updateFurniture, deleteFurniture
} from '../../services/api';
import { ProjectForm, ProductForm, EventForm, ArtForm, FurnitureForm } from './Forms';
import {
  DashboardContainer,
  Header,
  Title,
  TabsContainer,
  Tab,
  Button,
  Table,
  Th,
  Td,
  ActionButton,
  Overlay
} from './DashboardStyles';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [items, setItems] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchItems = async () => {
    setIsLoading(true);
    setError('');
    try {
      let data;
      switch (activeTab) {
        case 'projects':
          data = await getProjects();
          break;
        case 'products':
          data = await getProducts();
          break;
        case 'events':
          data = await getEvents();
          break;
        case 'art':
          data = await getArt();
          break;
        case 'furniture':
          data = await getFurniture();
          break;
        default:
          data = [];
      }
      setItems(data);
    } catch (err) {
      setError(`Failed to fetch ${activeTab}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAdminAuthenticated');
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    
    fetchItems();
  }, [activeTab, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const handleEdit = (item) => {
    setCurrentItem(item);
    setIsFormOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        if (activeTab === 'projects') {
          await deleteProject(itemId);
        } else if (activeTab === 'products') {
          await deleteProduct(itemId);
        } else if (activeTab === 'events') {
          await deleteEvent(itemId);
        } else if (activeTab === 'art') {
          await deleteArt(itemId);
        } else if (activeTab === 'furniture') {
          await deleteFurniture(itemId);
        }
        await fetchItems();
      } catch (err) {
        setError(`Failed to delete ${activeTab}`);
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentItem(null);
    fetchItems();
  };

  const handleAddClick = () => {
    console.log('Opening form...');
    setCurrentItem(null);
    setIsFormOpen(true);
  };

  const renderForm = () => {
    if (!isFormOpen) {
      return null;
    }

    const formProps = {
      currentItem,
      onClose: handleFormClose,
      onSubmit: async (formData) => {
        try {
          setIsLoading(true);
          console.log('Submitting form data:', formData);
          
          if (activeTab === 'projects') {
            if (currentItem) {
              await updateProject(currentItem._id, formData);
            } else {
              await createProject(formData);
            }
          } else if (activeTab === 'products') {
            if (currentItem) {
              await updateProduct(currentItem._id, formData);
            } else {
              await createProduct(formData);
            }
          } else if (activeTab === 'events') {
            if (currentItem) {
              await updateEvent(currentItem._id, formData);
            } else {
              await createEvent(formData);
            }
          } else if (activeTab === 'art') {
            if (currentItem) {
              await updateArt(currentItem._id, formData);
            } else {
              console.log('Submitting art data:', formData);
              await createArt(formData);
            }
          } else if (activeTab === 'furniture') {
            if (currentItem) {
              await updateFurniture(currentItem._id, formData);
            } else {
              await createFurniture(formData);
            }
          }
          setIsLoading(false);
          handleFormClose();
          await fetchItems();
        } catch (err) {
          console.error('Form submission error:', err);
          setIsLoading(false);
          setError(err.message || `Failed to ${currentItem ? 'update' : 'create'} ${activeTab}`);
        }
      }
    };

    switch (activeTab) {
      case 'projects':
        return <ProjectForm {...formProps} />;
      case 'products':
        return <ProductForm {...formProps} />;
      case 'events':
        return <EventForm {...formProps} />;
      case 'art':
        return <ArtForm {...formProps} />;
      case 'furniture':
        return <FurnitureForm {...formProps} />;
      default:
        return null;
    }
  };

  const renderTableHeaders = () => {
    switch (activeTab) {
      case 'projects':
        return (
          <tr>
            <Th>Title</Th>
            <Th>Location</Th>
            <Th>Tags</Th>
            <Th>Actions</Th>
          </tr>
        );
      case 'products':
        return (
          <tr>
            <Th>Name</Th>
            <Th>Price</Th>
            <Th>Color</Th>
            <Th>Actions</Th>
          </tr>
        );
      case 'events':
        return (
          <tr>
            <Th>Title</Th>
            <Th>Date</Th>
            <Th>Category</Th>
            <Th>Actions</Th>
          </tr>
        );
      case 'art':
        return (
          <tr>
            <Th>Title</Th>
            <Th>Category</Th>
            <Th>Year</Th>
            <Th>Actions</Th>
          </tr>
        );
      case 'furniture':
        return (
          <tr>
            <Th>Title</Th>
            <Th>Client</Th>
            <Th>Year</Th>
            <Th>Actions</Th>
          </tr>
        );
      default:
        return null;
    }
  };

  const renderTableRow = (item) => {
    switch (activeTab) {
      case 'projects':
        return (
          <tr key={item._id}>
            <Td>{item.title}</Td>
            <Td>{item.location}</Td>
            <Td>{item.tags}</Td>
            <Td>
              <ActionButton edit onClick={() => handleEdit(item)}>
                Edit
              </ActionButton>
              <ActionButton delete onClick={() => handleDelete(item._id)}>
                Delete
              </ActionButton>
            </Td>
          </tr>
        );
      case 'products':
        return (
          <tr key={item._id}>
            <Td>{item.name}</Td>
            <Td>${item.price?.toFixed(2)}</Td>
            <Td>{item.color}</Td>
            <Td>
              <ActionButton edit onClick={() => handleEdit(item)}>
                Edit
              </ActionButton>
              <ActionButton delete onClick={() => handleDelete(item._id)}>
                Delete
              </ActionButton>
            </Td>
          </tr>
        );
      case 'events':
        return (
          <tr key={item.id}>
            <Td>{item.title}</Td>
            <Td>{item.date}</Td>
            <Td>{item.category}</Td>
            <Td>
              <ActionButton edit onClick={() => handleEdit(item)}>
                Edit
              </ActionButton>
              <ActionButton delete onClick={() => handleDelete(item._id)}>
                Delete
              </ActionButton>
            </Td>
          </tr>
        );
      case 'art':
        return (
          <tr key={item.id}>
            <Td>{item.title}</Td>
            <Td>{item.category}</Td>
            <Td>{item.year}</Td>
            <Td>
              <ActionButton edit onClick={() => handleEdit(item)}>
                Edit
              </ActionButton>
              <ActionButton delete onClick={() => handleDelete(item._id)}>
                Delete
              </ActionButton>
            </Td>
          </tr>
        );
      case 'furniture':
        return (
          <tr key={item.id}>
            <Td>{item.title}</Td>
            <Td>{item.client}</Td>
            <Td>{item.year}</Td>
            <Td>
              <ActionButton edit onClick={() => handleEdit(item)}>
                Edit
              </ActionButton>
              <ActionButton delete onClick={() => handleDelete(item._id)}>
                Delete
              </ActionButton>
            </Td>
          </tr>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardContainer>
      <Header>
        <Title>Admin Dashboard</Title>
        <Button onClick={handleLogout}>Logout</Button>
      </Header>

      <TabsContainer>
        <Tab 
          active={(activeTab === 'projects').toString()}
          onClick={() => setActiveTab('projects')}
        > 
          Projects  
        </Tab>
        <Tab 
          active={(activeTab === 'products').toString()}
          onClick={() => setActiveTab('products')}
        >
          Products
        </Tab>
        <Tab 
          active={(activeTab === 'events').toString()}
          onClick={() => setActiveTab('events')}
        >
          Events
        </Tab>
        <Tab 
          active={(activeTab === 'art').toString()}
          onClick={() => setActiveTab('art')}
        >
          Art
        </Tab>
        <Tab 
          active={(activeTab === 'furniture').toString()}
          onClick={() => setActiveTab('furniture')}
        >
          Furniture
        </Tab>
      </TabsContainer>

      <Button onClick={handleAddClick} style={{ marginBottom: '24px' }}>
        Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
      </Button>

      {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Table>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {items.map(item => renderTableRow(item))}
          </tbody>
        </Table>
      )}

      {isFormOpen && (
        <Overlay>
          {renderForm()}
        </Overlay>
      )}
    </DashboardContainer>
  );
};

export default Dashboard; 